import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { parse } from 'query-string';
import { useAuth0 } from '@auth0/auth0-react';
import { any, forEachObjIndexed } from 'ramda';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import LoaderWrapper from '../../components/LoaderWrapper';
import { Routes } from '../../common/enums/Routes';
import PrivateRoute from '../PrivateRoute';
import { Switch, Redirect, withRouter, RouteComponentProps, matchPath } from 'react-router-dom';
import Header from '../Header';
import { IAppState } from '../../common/models/store/IAppState';
import DashboardPage from '../../scenes/Dashboard/containers/DashboardPage';
import PublicRoute from '../PublicRoute';
import { fetchCurrentUserRoutine } from '../../routines/user';
import NotFound from '../../scenes/NotFound';
import SurveyPreview from '../SurveyPreview';
import Survey from '../../scenes/SurveyUI/containers/Survey';
import CandidateForm from '../../scenes/CandidateForm/containers/CandidateForm';
import Settings from '../../scenes/Settings/containers/Settings';
import ModalWindow from '../../components/ModalWindow';
import { setReportCandidatesRoutine } from '../ShareReportModal/routines';
import ShareReportModal from '../ShareReportModal';
import SurveyBuilder, { ISurveyBuilderHandle } from '../../scenes/SurveyBuilder/containers/SurveyBuilder';
import PositionProfile from '../../scenes/PositionProfile/containers/PositionProfile';
import ContactProfile from '../../scenes/ContactProfile/containers/ContactProfile';
import RequestBuilder, { IReqBuilderHandle } from '../../scenes/RequestBuilder/containers/RequestBuilder';
import { toggleRequestBuilderRoutine } from '../../scenes/RequestBuilder/routines';
import { hideContactProfileRoutine } from '../../scenes/Dashboard/routines';
import { resetRoleRoutine, setRoleRoutine } from '../../scenes/ContactProfile/routines';
import { setNewPositionNameRoutine, togglePositionProfileRoutine } from '../../scenes/PositionProfile/routines';
import { hideSurveyBuilderRoutine, showSurveyBuilderRoutine } from '../../scenes/SurveyBuilder/routines';
import { setAuthApi } from '../../common/helpers/authHelper';
import { WorkspaceSettings } from '../../common/models/workspace/WorkspaceSettings';
import { defaultPalette, neutralGrayPalette } from '../../common/helpers/colorHelper';
import InactiveUserPage from '../../scenes/InactiveUserPage';
import { routeTitles, setContactProfileTitle, setPositionProfileTitle, setRouteTitle } from './utils';
import { IContact } from '../../common/models/contact/IContact';
import styles from './styles.module.scss';
import SurveyManualInput from '../SurveyManualInput';
import IntegrationCallback from '../IntegrationCallback';

interface IRoutingProps extends RouteComponentProps {
  loading: boolean;
  isAuthorized: boolean;
  reportCandidateIds: string[] | undefined;
  contactId: string;
  contact: IContact;
  positionId?: string;
  positionNames: {
    current?: string;
    previous?: string;
  };
  isRequestBuilderShown: boolean;
  isSurveyBuilderShown: boolean;
  workspaceSettings: WorkspaceSettings;
  showWorkspaceColors: boolean;
  toggleRequestBuilder: IBindingCallback1<boolean>;
  showSurveyBuilder: IBindingCallback1<string>;
  hideSurveyBuilder: IBindingAction;
  onHideContactProfileModal: IBindingAction;
  setRole: IBindingCallback1<any>;
  resetRole: IBindingAction;
  togglePositionProfile: IBindingCallback1<any>;
  setNewPositionName: IBindingCallback1<{ id: string; name: string }>;
  closeReportModal: IBindingAction;
  fetchCurrentUser: IBindingAction;
}

const Routing: FunctionComponent<IRoutingProps> = ({
  contact,
  loading: isUserLoading,
  location,
  history,
  isAuthorized,
  reportCandidateIds,
  fetchCurrentUser,
  closeReportModal,
  contactId,
  positionId,
  positionNames,
  isRequestBuilderShown,
  isSurveyBuilderShown,
  workspaceSettings,
  showWorkspaceColors,
  showSurveyBuilder,
  hideSurveyBuilder,
  toggleRequestBuilder,
  onHideContactProfileModal,
  setRole,
  resetRole,
  togglePositionProfile,
  setNewPositionName
}) => {
  const auth0 = useAuth0();
  setAuthApi(auth0);

  const locationsWithoutHeader = [Routes.SurveyPreview,
    Routes.SurveyUI,
    Routes.CandidateForm,
    Routes.CandidateFormPreview,
    Routes.IntegrationsCallback,
    `${Routes.ManualInput}/:id`];
  const queryParams = parse(location.search);
  const reqBuilderRef = useRef<IReqBuilderHandle>(null);
  const surveyBuilderRef = useRef<ISurveyBuilderHandle>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (auth0.isAuthenticated && !isAuthorized) {
      fetchCurrentUser();
    }
  }, [auth0.isAuthenticated, isAuthorized]);

  useEffect(() => {
    if (auth0.error?.message === 'Signup forbidden') {
      history.push('/');
    } else if (auth0.error && !auth0.isAuthenticated) {
      history.push('/404');
    }
  }, [auth0.error]);

  useEffect(() => {
    if ((!auth0.isAuthenticated && !auth0.isLoading) || (isAuthorized && !isUserLoading)) {
      setLoading(false);
    }
  });

  useEffect(() => {
    const { contactId: queryContactId, role } = queryParams;
    if (role && queryContactId) {
      setRole({ ...queryParams });
    } else if (!queryContactId) {
      resetRole();
    }
  }, [queryParams.contactId]);

  useEffect(() => {
    toggleRequestBuilder(Boolean(queryParams.reqBuilderShown));
  }, [queryParams.reqBuilderShown]);

  useEffect(() => {
    togglePositionProfile(queryParams.positionId);
  }, [queryParams.positionId]);

  useEffect(() => {
    if (queryParams.surveyBuilderShown) {
      showSurveyBuilder(queryParams.surveyId as string);
    } else {
      hideSurveyBuilder();
    }
  }, [queryParams.surveyBuilderShown]);

  const clearSearchParams = () => {
    if ((location.state as Record<string, any>)?.from) history.goBack();
    else history.push({ ...location, search: '' });
  };

  const onHideContactModal = () => {
    onHideContactProfileModal();
    clearSearchParams();
  };

  const confirmReqBuilderClose = () => reqBuilderRef.current?.confirmClose();

  const confirmSurveyBuilderClose = () => surveyBuilderRef.current?.confirmClose();

  const onHideRequestBuilderModal = () => {
    toggleRequestBuilder(false);
    if (queryParams.reqBuilderShown) clearSearchParams();
  };

  const onHidePositionModal = () => {
    togglePositionProfile(null);
    clearSearchParams();
    const { current, previous } = positionNames;
    if (current !== previous && current && positionId) {
      setNewPositionName({ name: current, id: positionId });
    }
  };

  const onHideSurveyModal = () => {
    clearSearchParams();
  };

  useEffect(() => {
    const variables = showWorkspaceColors
      ? { ...neutralGrayPalette, ...(workspaceSettings || {}) }
      : defaultPalette;
    forEachObjIndexed((val, key) => (
      document.documentElement.style.setProperty(key, val)
    ), variables);
  }, [workspaceSettings, showWorkspaceColors]);

  useEffect(() => {
    if (queryParams.reqBuilderShown) {
      document.title = routeTitles.refReq;
    } else if (queryParams.contactId) {
      setContactProfileTitle(contact);
    } else if (queryParams.positionId) {
      setPositionProfileTitle(positionNames.current);
    } else {
      setRouteTitle(location.pathname);
    }
  }, [location.pathname, location.search, contact, positionNames.current]);

  return (
    <LoaderWrapper loading={loading}>
      {isAuthorized && (
        !any(route => Boolean(matchPath(location.pathname, route)), locationsWithoutHeader) && <Header />
      )}
      <Switch>
        {isAuthorized && <Redirect exact from="/" to={Routes.Dashboard} />}
        <PublicRoute restrict exact path={Routes.Callback} component={() => <LoaderWrapper loading />} />
        <PublicRoute path={Routes.SurveyUI} component={Survey} />
        <PublicRoute path={Routes.CandidateForm} component={CandidateForm} />
        {auth0.error && <PublicRoute path={Routes.InactiveUserPage} component={InactiveUserPage} />}
        <PrivateRoute path={Routes.Dashboard} component={DashboardPage} />
        <PrivateRoute path={Routes.Settings} component={Settings} />
        <PrivateRoute path={Routes.SurveyPreview} component={SurveyPreview} />
        <PrivateRoute path={`${Routes.ManualInput}/:id`} component={SurveyManualInput} />
        <PrivateRoute path={Routes.IntegrationsCallback} component={IntegrationCallback} />
        <PrivateRoute path="*" component={NotFound} />
      </Switch>
      {isAuthorized && (
        <>
          <ModalWindow
            contentClassName="medium-modal report-modal"
            isShown={Boolean(reportCandidateIds)}
            onHide={() => closeReportModal()}
          >
            <ShareReportModal />
          </ModalWindow>
          <ModalWindow
            isShown={isRequestBuilderShown}
            onHide={confirmReqBuilderClose}
            contentClassName="big-modal modal-wrapper"
          >
            <RequestBuilder ref={reqBuilderRef} onHideRequestBuilder={onHideRequestBuilderModal} />
          </ModalWindow>
          <ModalWindow
            isShown={Boolean(contactId)}
            onHide={onHideContactModal}
            contentClassName="profile-modal-content big-modal"
          >
            <ContactProfile />
          </ModalWindow>
          <ModalWindow
            isShown={Boolean(positionId)}
            onHide={onHidePositionModal}
            contentClassName={`profile-modal-content big-modal ${styles.positionProfile}`}
          >
            <PositionProfile />
          </ModalWindow>
          <ModalWindow
            isShown={Boolean(queryParams.surveyBuilderShown) && isSurveyBuilderShown}
            onHide={confirmSurveyBuilderClose}
            contentClassName="big-modal sb-modal-wrapper"
          >
            <SurveyBuilder ref={surveyBuilderRef} hideSurveyBuilder={onHideSurveyModal} />
          </ModalWindow>
        </>
      )}
    </LoaderWrapper>
  );
};

const mapStateToProps = ({
  user: { isAuthorized, loading },
  report: { candidateIds: reportCandidateIds },
  contactProfile: { contact: { contactId, contact } },
  positionProfile: { positionId, positionName, position },
  requestBuilder: { isShown: isRequestBuilderShown },
  surveyBuilder: { isShown: isSurveyBuilderShown },
  workspace: { settings, showWorkspaceColors }
}: IAppState) => ({
  isAuthorized,
  loading,
  reportCandidateIds,
  contactId,
  contact,
  isRequestBuilderShown,
  positionId,
  positionNames: {
    current: positionName,
    previous: position?.name
  },
  isSurveyBuilderShown,
  workspaceSettings: settings,
  showWorkspaceColors
});

const mapDispatchToProps = {
  fetchCurrentUser: fetchCurrentUserRoutine,
  closeReportModal: setReportCandidatesRoutine,
  toggleRequestBuilder: toggleRequestBuilderRoutine,
  onHideContactProfileModal: hideContactProfileRoutine,
  setRole: setRoleRoutine,
  resetRole: resetRoleRoutine,
  togglePositionProfile: togglePositionProfileRoutine,
  showSurveyBuilder: showSurveyBuilderRoutine,
  hideSurveyBuilder: hideSurveyBuilderRoutine,
  setNewPositionName: setNewPositionNameRoutine
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Routing));
